import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  QUESTION_TYPES,
  VALIDATION_TYPES,
  COMPARISON_OPERATORS,
  COMPOSITE_DELIMITERS,
  useQuestionSectionMap,
  toCamelCase,
} from '@/features/quote-and-apply-builder';

interface UseQuestionSettingsProps {
  question: PolicyFormQuestion;
  allQuestions: Record<string, PolicyFormQuestion>;
  isNew?: boolean;
  onSave: (updates: Partial<PolicyFormQuestion>) => void;
  onClose: () => void;
}

export function useQuestionSettings({
  question,
  allQuestions,
  isNew,
  onSave,
  onClose,
}: UseQuestionSettingsProps) {
  const questionSectionMap = useQuestionSectionMap();

  // Basic settings
  const [title, setTitle] = useState(isNew ? '' : (question.title || ''));
  const [questionRef, setQuestionRef] = useState(isNew ? '' : (question.questionRef || ''));
  const [isQuestionRefManuallyEdited, setIsQuestionRefManuallyEdited] = useState(false);
  const [questionRefError, setQuestionRefError] = useState('');
  const [type, setType] = useState<PolicyFormQuestion['type']>(question.type);
  const [required, setRequired] = useState(question.required || false);
  const [placeholder, setPlaceholder] = useState(question.placeholder || '');
  const [helperText, setHelperText] = useState(question.helperText || '');
  const [validationType, setValidationType] = useState<
    PolicyFormQuestion['validationType'] | undefined
  >(question.validationType);

  // Advanced settings
  const [titleOverride, setTitleOverride] = useState(question.titleOverride || '');
  const [conditionalTitle, setConditionalTitle] = useState(question.conditionalTitle || '');
  const [conditionalHelperText, setConditionalHelperText] = useState(
    question.conditionalHelperText || '',
  );
  const [format, setFormat] = useState<PolicyFormQuestion['format']>(question.format);
  const [disabled, setDisabled] = useState(question.disabled || false);
  const [hidden, setHidden] = useState(question.hidden || false);
  const [overflow, setOverflow] = useState(question.overflow || false);
  const [gridColumns, setGridColumns] = useState<number | undefined>(question.gridColumns);
  const [characterLimit, setCharacterLimit] = useState(question.characterLimit);

  // Options (for select, radio, multiselect, textselect)
  const [options, setOptions] = useState(question.options || []);

  // Table settings
  const [columns, setColumns] = useState<PolicyFormTableColumn[]>(question.columns || []);
  const [minRows, setMinRows] = useState<number | undefined>(question.minRows);
  const [maxRows, setMaxRows] = useState<number | undefined>(question.maxRows);

  // Composite settings
  const [hasComposite, setHasComposite] = useState(!!question.composite);
  const [compositeQuestionRefs, setCompositeQuestionRefs] = useState<string[]>(
    question.composite?.questionRefs || [],
  );
  const [compositeDelimiter, setCompositeDelimiter] = useState<CompositeDelimiter>(
    question.composite?.delimiter || ' ',
  );
  const [compositeIncludeMetadata, setCompositeIncludeMetadata] = useState(
    question.composite?.includeMetadata || false,
  );
  const [compositeAllowAppendText, setCompositeAllowAppendText] = useState(
    question.composite?.allowAppendText || false,
  );
  const [compositeFieldRef, setCompositeFieldRef] = useState(
    question.composite?.compositeFieldRef || '',
  );
  const [compositeTitle, setCompositeTitle] = useState(question.composite?.compositeTitle || '');
  const [compositeMapping, setCompositeMapping] = useState<Record<string, string>>(
    question.composite?.mapping || {},
  );
  const [hasCompositeCondition, setHasCompositeCondition] = useState(
    !!question.composite?.conditional,
  );
  const [compositeConditionType, setCompositeConditionType] = useState<'simple' | 'logical'>(
    question.composite?.conditional && 'conditions' in question.composite.conditional
      ? 'logical'
      : 'simple',
  );
  const [compositeSimpleCondition, setCompositeSimpleCondition] = useState<FieldCondition>(() => {
    const cond = question.composite?.conditional;
    if (cond && !('conditions' in cond)) {
      return cond as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [compositeLogicalCondition, setCompositeLogicalCondition] = useState<LogicalCondition>(
    () => {
      const cond = question.composite?.conditional;
      if (cond && 'conditions' in cond) {
        return cond as LogicalCondition;
      }
      return { operator: 'and', conditions: [] };
    },
  );

  // Conditional logic
  const [hasVisibleCondition, setHasVisibleCondition] = useState(!!question.conditional?.visible);
  const [hasRequiredCondition, setHasRequiredCondition] = useState(
    !!question.conditional?.required,
  );
  const [hasShowConditionalTextCondition, setHasShowConditionalTextCondition] = useState(
    !!question.conditional?.showConditionalText,
  );

  // Visible condition
  const [visibleConditionType, setVisibleConditionType] = useState<'simple' | 'logical'>(
    question.conditional?.visible && 'conditions' in question.conditional.visible
      ? 'logical'
      : 'simple',
  );
  const [visibleSimpleCondition, setVisibleSimpleCondition] = useState<FieldCondition>(() => {
    const visible = question.conditional?.visible;
    if (visible && !('conditions' in visible)) {
      return visible as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [visibleLogicalCondition, setVisibleLogicalCondition] = useState<LogicalCondition>(() => {
    const visible = question.conditional?.visible;
    if (visible && 'conditions' in visible) {
      return visible as LogicalCondition;
    }
    return { operator: 'and', conditions: [] };
  });

  // Required condition
  const [requiredConditionType, setRequiredConditionType] = useState<'simple' | 'logical'>(
    question.conditional?.required && 'conditions' in question.conditional.required
      ? 'logical'
      : 'simple',
  );
  const [requiredSimpleCondition, setRequiredSimpleCondition] = useState<FieldCondition>(() => {
    const req = question.conditional?.required;
    if (req && !('conditions' in req)) {
      return req as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [requiredLogicalCondition, setRequiredLogicalCondition] = useState<LogicalCondition>(() => {
    const req = question.conditional?.required;
    if (req && 'conditions' in req) {
      return req as LogicalCondition;
    }
    return { operator: 'and', conditions: [] };
  });

  // ShowConditionalText condition
  const [showTextConditionType, setShowTextConditionType] = useState<'simple' | 'logical'>(
    question.conditional?.showConditionalText &&
      'conditions' in question.conditional.showConditionalText
      ? 'logical'
      : 'simple',
  );
  const [showTextSimpleCondition, setShowTextSimpleCondition] = useState<FieldCondition>(() => {
    const cond = question.conditional?.showConditionalText;
    if (cond && !('conditions' in cond)) {
      return cond as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [showTextLogicalCondition, setShowTextLogicalCondition] = useState<LogicalCondition>(() => {
    const cond = question.conditional?.showConditionalText;
    if (cond && 'conditions' in cond) {
      return cond as LogicalCondition;
    }
    return { operator: 'and', conditions: [] };
  });

  useEffect(() => {
    setTitle(isNew ? '' : (question.title || ''));
    setQuestionRef(isNew ? '' : (question.questionRef || ''));
    setIsQuestionRefManuallyEdited(false);
    setQuestionRefError('');
    setType(question.type);
    setRequired(question.required || false);
    setPlaceholder(question.placeholder || '');
    setHelperText(question.helperText || '');
    setValidationType(question.validationType);
    setTitleOverride(question.titleOverride || '');
    setConditionalTitle(question.conditionalTitle || '');
    setConditionalHelperText(question.conditionalHelperText || '');
    setFormat(question.format);
    setDisabled(question.disabled || false);
    setHidden(question.hidden || false);
    setOverflow(question.overflow || false);
    setGridColumns(question.gridColumns);
    setCharacterLimit(question.characterLimit);
    setOptions(question.options || []);

    setColumns(question.columns || []);
    setMinRows(question.minRows);
    setMaxRows(question.maxRows);

    setHasComposite(!!question.composite);
    setCompositeQuestionRefs(question.composite?.questionRefs || []);
    setCompositeDelimiter(question.composite?.delimiter || ' ');
    setCompositeIncludeMetadata(question.composite?.includeMetadata || false);
    setCompositeAllowAppendText(question.composite?.allowAppendText || false);
    setCompositeFieldRef(question.composite?.compositeFieldRef || '');
    setCompositeTitle(question.composite?.compositeTitle || '');
    setCompositeMapping(question.composite?.mapping || {});
    setHasCompositeCondition(!!question.composite?.conditional);

    const compositeCond = question.composite?.conditional;
    if (compositeCond) {
      if ('conditions' in compositeCond) {
        setCompositeConditionType('logical');
        setCompositeLogicalCondition(compositeCond as LogicalCondition);
      } else {
        setCompositeConditionType('simple');
        setCompositeSimpleCondition(compositeCond as FieldCondition);
      }
    } else {
      setCompositeConditionType('simple');
      setCompositeSimpleCondition({ field: '', operator: 'equals', value: '' });
      setCompositeLogicalCondition({ operator: 'and', conditions: [] });
    }

    setHasVisibleCondition(!!question.conditional?.visible);
    setHasRequiredCondition(!!question.conditional?.required);
    setHasShowConditionalTextCondition(!!question.conditional?.showConditionalText);

    const visible = question.conditional?.visible;
    if (visible) {
      if ('conditions' in visible) {
        setVisibleConditionType('logical');
        setVisibleLogicalCondition(visible as LogicalCondition);
      } else {
        setVisibleConditionType('simple');
        setVisibleSimpleCondition(visible as FieldCondition);
      }
    } else {
      setVisibleConditionType('simple');
      setVisibleSimpleCondition({ field: '', operator: 'equals', value: '' });
      setVisibleLogicalCondition({ operator: 'and', conditions: [] });
    }

    const req = question.conditional?.required;
    if (req) {
      if ('conditions' in req) {
        setRequiredConditionType('logical');
        setRequiredLogicalCondition(req as LogicalCondition);
      } else {
        setRequiredConditionType('simple');
        setRequiredSimpleCondition(req as FieldCondition);
      }
    } else {
      setRequiredConditionType('simple');
      setRequiredSimpleCondition({ field: '', operator: 'equals', value: '' });
      setRequiredLogicalCondition({ operator: 'and', conditions: [] });
    }

    const showText = question.conditional?.showConditionalText;
    if (showText) {
      if ('conditions' in showText) {
        setShowTextConditionType('logical');
        setShowTextLogicalCondition(showText as LogicalCondition);
      } else {
        setShowTextConditionType('simple');
        setShowTextSimpleCondition(showText as FieldCondition);
      }
    } else {
      setShowTextConditionType('simple');
      setShowTextSimpleCondition({ field: '', operator: 'equals', value: '' });
      setShowTextLogicalCondition({ operator: 'and', conditions: [] });
    }
  }, [question, isNew]);

  const questionOptions = useMemo(
    () =>
      Object.values(allQuestions)
        .filter(otherQuestion => otherQuestion.questionRef !== question.questionRef)
        .map(otherQuestion => ({
          value: otherQuestion.questionRef,
          label: otherQuestion.questionRef,
          selectedDisplay: otherQuestion.questionRef,
          optionDisplay: (
            <div className="flex w-64 flex-col gap-1">
              <span className="text-sm font-medium text-gray-950">{otherQuestion.questionRef}</span>
              <span className="text-foreground truncate text-xs">{otherQuestion.title}</span>
              {questionSectionMap[otherQuestion.questionRef] && (
                <span className="text-primary truncate text-xs">
                  {questionSectionMap[otherQuestion.questionRef]}
                </span>
              )}
            </div>
          ),
        })),
    [allQuestions, question.questionRef, questionSectionMap],
  );

  const hasOptions = ['select', 'radio', 'multiselect', 'textselect'].includes(type);

  const compositeDisabledTypes: PolicyFormQuestion['type'][] = useMemo(
    () => ['select', 'textselect', 'multiselect', 'table', 'textarea-text', 'textarea-append'],
    [],
  );
  const supportsComposite = !compositeDisabledTypes.includes(type);

  const handleTitleChange = useCallback(
    (value: string) => {
      setTitle(value);
      if (isNew && !isQuestionRefManuallyEdited) {
        setQuestionRef(toCamelCase(value));
      }
    },
    [isNew, isQuestionRefManuallyEdited],
  );

  const handleQuestionRefChange = useCallback(
    (value: string) => {
      setQuestionRef(value);
      setIsQuestionRefManuallyEdited(true);
      if (!value.trim()) {
        setQuestionRefError('Question ID is required');
      } else if (value !== question.questionRef && Object.keys(allQuestions).includes(value)) {
        setQuestionRefError('A question with this ID already exists');
      } else {
        setQuestionRefError('');
      }
    },
    [allQuestions, question.questionRef],
  );

  const handleTypeChange = useCallback(
    (newType: PolicyFormQuestion['type']) => {
      setType(newType);
      if (compositeDisabledTypes.includes(newType)) {
        setHasComposite(false);
        setCompositeQuestionRefs([]);
        setCompositeDelimiter(' ');
        setCompositeIncludeMetadata(false);
        setCompositeAllowAppendText(false);
        setCompositeFieldRef('');
        setCompositeTitle('');
        setCompositeMapping({});
        setHasCompositeCondition(false);
      }
    },
    [compositeDisabledTypes],
  );

  const handleSave = useCallback(() => {
    if (!questionRef.trim()) {
      setQuestionRefError('Question ID is required');
      return;
    }

    const isDuplicate =
      questionRef !== question.questionRef && Object.keys(allQuestions).includes(questionRef);
    if (isDuplicate) {
      setQuestionRefError('A question with this ID already exists');
      return;
    }

    let conditional: ConditionalBehavior | undefined;

    const visibleCond = hasVisibleCondition
      ? visibleConditionType === 'simple' && visibleSimpleCondition.field
        ? visibleSimpleCondition
        : visibleConditionType === 'logical' && visibleLogicalCondition.conditions.length > 0
          ? visibleLogicalCondition
          : undefined
      : undefined;

    const requiredCond = hasRequiredCondition
      ? requiredConditionType === 'simple' && requiredSimpleCondition.field
        ? requiredSimpleCondition
        : requiredConditionType === 'logical' && requiredLogicalCondition.conditions.length > 0
          ? requiredLogicalCondition
          : undefined
      : undefined;

    const showTextCond = hasShowConditionalTextCondition
      ? showTextConditionType === 'simple' && showTextSimpleCondition.field
        ? showTextSimpleCondition
        : showTextConditionType === 'logical' && showTextLogicalCondition.conditions.length > 0
          ? showTextLogicalCondition
          : undefined
      : undefined;

    if (visibleCond || requiredCond || showTextCond) {
      conditional = {};
      if (visibleCond) conditional.visible = visibleCond;
      if (requiredCond) conditional.required = requiredCond;
      if (showTextCond) conditional.showConditionalText = showTextCond;
    }

    let composite: CompositeConfiguration | undefined;
    if (hasComposite && compositeQuestionRefs.length > 0) {
      const compositeCond = hasCompositeCondition
        ? compositeConditionType === 'simple' && compositeSimpleCondition.field
          ? compositeSimpleCondition
          : compositeConditionType === 'logical' && compositeLogicalCondition.conditions.length > 0
            ? compositeLogicalCondition
            : undefined
        : undefined;

      composite = {
        questionRefs: compositeQuestionRefs,
        delimiter: compositeDelimiter,
      };
      if (compositeIncludeMetadata) composite.includeMetadata = true;
      if (compositeAllowAppendText) composite.allowAppendText = true;
      if (compositeFieldRef) composite.compositeFieldRef = compositeFieldRef;
      if (compositeTitle) composite.compositeTitle = compositeTitle;
      if (Object.keys(compositeMapping).length > 0) composite.mapping = compositeMapping;
      if (compositeCond) composite.conditional = compositeCond;
    }

    onSave({
      title,
      questionRef,
      type,
      required,
      placeholder: placeholder || undefined,
      helperText: helperText || undefined,
      validationType: validationType || undefined,
      titleOverride: titleOverride || undefined,
      conditionalTitle: conditionalTitle || undefined,
      conditionalHelperText: conditionalHelperText || undefined,
      format: format || undefined,
      disabled: disabled || undefined,
      hidden: hidden || undefined,
      overflow: overflow || undefined,
      gridColumns: gridColumns || undefined,
      characterLimit: characterLimit || undefined,
      options: hasOptions ? options : undefined,
      columns: type === 'table' ? columns : undefined,
      minRows: type === 'table' ? minRows : undefined,
      maxRows: type === 'table' ? maxRows : undefined,
      conditional,
      composite,
    });
    onClose();
  }, [
    hasVisibleCondition,
    visibleConditionType,
    visibleSimpleCondition,
    visibleLogicalCondition,
    hasRequiredCondition,
    requiredConditionType,
    requiredSimpleCondition,
    requiredLogicalCondition,
    hasShowConditionalTextCondition,
    showTextConditionType,
    showTextSimpleCondition,
    showTextLogicalCondition,
    hasComposite,
    compositeQuestionRefs,
    hasCompositeCondition,
    compositeConditionType,
    compositeSimpleCondition,
    compositeLogicalCondition,
    compositeDelimiter,
    compositeIncludeMetadata,
    compositeAllowAppendText,
    compositeFieldRef,
    compositeTitle,
    compositeMapping,
    title,
    questionRef,
    type,
    required,
    placeholder,
    helperText,
    validationType,
    titleOverride,
    conditionalTitle,
    conditionalHelperText,
    format,
    disabled,
    hidden,
    overflow,
    gridColumns,
    characterLimit,
    hasOptions,
    options,
    columns,
    minRows,
    maxRows,
    onSave,
    onClose,
  ]);

  const handleAddCompositeQuestionRef = useCallback(
    (ref: string) => {
      if (ref && !compositeQuestionRefs.includes(ref)) {
        setCompositeQuestionRefs([...compositeQuestionRefs, ref]);
      }
    },
    [compositeQuestionRefs],
  );

  const handleRemoveCompositeQuestionRef = useCallback(
    (index: number) => {
      setCompositeQuestionRefs(compositeQuestionRefs.filter((_, refIndex) => refIndex !== index));
    },
    [compositeQuestionRefs],
  );

  const handleUpdateMapping = useCallback(
    (mappingQuestionRef: string, mappedValue: string) => {
      if (mappedValue) {
        setCompositeMapping({ ...compositeMapping, [mappingQuestionRef]: mappedValue });
      } else {
        const newMapping = { ...compositeMapping };
        delete newMapping[mappingQuestionRef];
        setCompositeMapping(newMapping);
      }
    },
    [compositeMapping],
  );

  return {
    // Basic settings
    title,
    setTitle,
    questionRef,
    setQuestionRef,
    questionRefError,
    type,
    required,
    setRequired,
    placeholder,
    setPlaceholder,
    helperText,
    setHelperText,
    validationType,
    setValidationType,

    // Advanced settings
    titleOverride,
    setTitleOverride,
    conditionalTitle,
    setConditionalTitle,
    conditionalHelperText,
    setConditionalHelperText,
    format,
    setFormat,
    disabled,
    setDisabled,
    hidden,
    setHidden,
    overflow,
    setOverflow,
    gridColumns,
    setGridColumns,
    characterLimit,
    setCharacterLimit,

    // Options
    options,
    setOptions,
    hasOptions,

    // Table
    columns,
    setColumns,
    minRows,
    setMinRows,
    maxRows,
    setMaxRows,

    // Composite
    hasComposite,
    setHasComposite,
    compositeQuestionRefs,
    compositeDelimiter,
    setCompositeDelimiter,
    compositeIncludeMetadata,
    setCompositeIncludeMetadata,
    compositeAllowAppendText,
    setCompositeAllowAppendText,
    compositeFieldRef,
    setCompositeFieldRef,
    compositeTitle,
    setCompositeTitle,
    compositeMapping,
    hasCompositeCondition,
    setHasCompositeCondition,
    compositeConditionType,
    setCompositeConditionType,
    compositeSimpleCondition,
    setCompositeSimpleCondition,
    compositeLogicalCondition,
    setCompositeLogicalCondition,
    supportsComposite,

    // Conditional logic
    hasVisibleCondition,
    setHasVisibleCondition,
    hasRequiredCondition,
    setHasRequiredCondition,
    hasShowConditionalTextCondition,
    setHasShowConditionalTextCondition,

    // Visible condition
    visibleConditionType,
    setVisibleConditionType,
    visibleSimpleCondition,
    setVisibleSimpleCondition,
    visibleLogicalCondition,
    setVisibleLogicalCondition,

    // Required condition
    requiredConditionType,
    setRequiredConditionType,
    requiredSimpleCondition,
    setRequiredSimpleCondition,
    requiredLogicalCondition,
    setRequiredLogicalCondition,

    // ShowConditionalText condition
    showTextConditionType,
    setShowTextConditionType,
    showTextSimpleCondition,
    setShowTextSimpleCondition,
    showTextLogicalCondition,
    setShowTextLogicalCondition,

    // Computed values
    questionOptions,

    // Constants
    questionTypes: QUESTION_TYPES,
    validationTypes: VALIDATION_TYPES,
    comparisonOperators: COMPARISON_OPERATORS,
    compositeDelimiters: COMPOSITE_DELIMITERS,

    // Handlers
    handleTitleChange,
    handleQuestionRefChange,
    handleTypeChange,
    handleSave,
    handleAddCompositeQuestionRef,
    handleRemoveCompositeQuestionRef,
    handleUpdateMapping,
  };
}
